import { useEffect, useState, useMemo } from "react";
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    SortingState,
    ColumnFiltersState,
} from "@tanstack/react-table";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/shadcn/table";
import { Input } from "@/components/shadcn/input";
import { Checkbox } from "@/components/shadcn/checkbox";
import { ArrowUp, ArrowDown, ArrowUpDown, Search } from "lucide-react";
import dayjs from "dayjs";
import { SelectMatchesDialogPagination } from "./SelectMatchesDialogPagination";
import { Match } from "@/api/tournaments";
import { matchColumns } from "./columns";

interface Props {
    matches: Match[];
    tournamentSelected: boolean;
    onSelectMatches?: (matchIds: number[]) => void;
    defaultSelectedMatchIds?: number[];
}

export function SelectMatchesDialogTable({
    matches,
    tournamentSelected,
    onSelectMatches,
    defaultSelectedMatchIds,
}: Props) {
    const [pageIndex, setPageIndex] = useState(0);
    const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
    const [sorting, setSorting] = useState<SortingState>([{ id: "date", desc: true }]);
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = useState("");

    const pageSize = 6;

    const filteredMatches = useMemo(() => {
        return matches.filter((match) => {
            if (!match.date) return false;
            if (match.status && match.status.toLowerCase() === "aborted") return false;
            return true;
        });
    }, [matches]);

    const table = useReactTable({
        data: filteredMatches,
        columns: matchColumns,
        manualPagination: false,
        enableColumnResizing: false,
        getRowId: (row) => String(row.id),
        state: {
            pagination: { pageIndex, pageSize },
            rowSelection,
            sorting,
            columnFilters,
            globalFilter,
        },
        onRowSelectionChange: (updater) => {
            const updated = typeof updater === "function" ? updater(rowSelection) : updater;
            setRowSelection(updated);

            const selectedMatchIds = Object.keys(updated)
                .filter((key) => updated[key])
                .map((key) => parseInt(key));

            if (onSelectMatches) {
                onSelectMatches(selectedMatchIds);
            }
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: (row, _columnId, filterValue: string) => {
            const search = filterValue.toLowerCase();
            const date = dayjs(row.original.date).format("DD/MM/YYYY").toLowerCase();
            const home = row.original.homeTeam.shortName.toLowerCase();
            const away = row.original.awayTeam.shortName.toLowerCase();

            return date.includes(search) || home.includes(search) || away.includes(search);
        },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        enableRowSelection: true,
        enableMultiRowSelection: true,
    });

    useEffect(() => {
        setPageIndex(0);
    }, [globalFilter, filteredMatches, tournamentSelected]);

    useEffect(() => {
        if (filteredMatches.length > 0) {
            const selectedRows: Record<string, boolean> = {};

            if (defaultSelectedMatchIds && defaultSelectedMatchIds.length > 0) {
                const filteredMatchIds = new Set(filteredMatches.map((match) => match.id));
                defaultSelectedMatchIds.forEach((matchId) => {
                    if (filteredMatchIds.has(matchId)) {
                        selectedRows[String(matchId)] = true;
                    }
                });
            }

            setRowSelection(selectedRows);
        }
    }, [defaultSelectedMatchIds, filteredMatches]);

    const filteredRows = table.getFilteredRowModel().rows;
    const filteredMatchIds = filteredRows.map((row) => row.original.id);
    const selectedFilteredCount = filteredMatchIds.filter((id) => rowSelection[String(id)]).length;
    const isAllFilteredSelected = filteredMatchIds.length > 0 && selectedFilteredCount === filteredMatchIds.length;
    const isIndeterminate = selectedFilteredCount > 0 && selectedFilteredCount < filteredMatchIds.length;

    const isSelectAllDisabled = !tournamentSelected || filteredMatches.length === 0 || filteredMatchIds.length === 0;

    const handleSelectAllFiltered = () => {
        if (isSelectAllDisabled) return;

        const newSelection: Record<string, boolean> = { ...rowSelection };

        if (isAllFilteredSelected) {
            filteredMatchIds.forEach((matchId) => {
                delete newSelection[String(matchId)];
            });
        } else {
            filteredMatchIds.forEach((matchId) => {
                newSelection[String(matchId)] = true;
            });
        }

        setRowSelection(newSelection);

        const selectedMatchIds = Object.keys(newSelection)
            .filter((key) => newSelection[key])
            .map((key) => parseInt(key));

        if (onSelectMatches) {
            onSelectMatches(selectedMatchIds);
        }
    };

    return (
        <div className="flex flex-col gap-4 h-[390px] w-[600px]">
            <div className="flex gap-3 items-center">
                <div className="relative w-[400px]">
                    <Search
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant"
                        style={{ width: "20px", height: "20px" }}
                    />
                    <Input
                        placeholder="Search by date, home team or away team..."
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        className="w-full pl-11"
                    />
                </div>
                <div className="w-[185px] flex items-center">
                    <label
                        className={`flex items-center gap-2 text-[13px] transition-colors mt-1 ${
                            isSelectAllDisabled
                                ? "text-on-surface-variant cursor-not-allowed"
                                : "text-on-surface hover:text-on-surface cursor-pointer"
                        }`}
                    >
                        <div className="relative">
                            <Checkbox
                                checked={isAllFilteredSelected}
                                onCheckedChange={handleSelectAllFiltered}
                                disabled={isSelectAllDisabled}
                                aria-label="Select all matches"
                                className={`${
                                    isSelectAllDisabled
                                        ? "opacity-50 cursor-not-allowed"
                                        : "data-[state=checked]:bg-primary data-[state=checked]:border-primary cursor-pointer mt-0.5"
                                }`}
                            />
                            {isIndeterminate && !isSelectAllDisabled && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="w-2 h-0.5 bg-white rounded"></div>
                                </div>
                            )}
                        </div>
                        {globalFilter ? `Select all filtered (${filteredMatchIds.length})` : "Select whole tournament"}
                    </label>
                </div>
            </div>
            <div className="relative w-full">
                <div className="overflow-hidden rounded-sm border border-outline-variant/20 bg-surface-container-low">
                    <Table className="w-full whitespace-nowrap">
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id} className="border-b border-outline-variant/20">
                                    {headerGroup.headers.map((header) => (
                                        <TableHead
                                            key={header.id}
                                            className="bg-surface-container-high hover:bg-surface-container-highest"
                                        >
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    className="flex items-center gap-1 cursor-pointer select-none"
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {header.column.getCanSort() && (
                                                        <>
                                                            {header.column.getIsSorted() === "asc" ? (
                                                                <ArrowUp className="h-3.5 w-3.5 text-on-surface" />
                                                            ) : header.column.getIsSorted() === "desc" ? (
                                                                <ArrowDown className="h-3.5 w-3.5 text-on-surface" />
                                                            ) : (
                                                                <ArrowUpDown className="h-3.5 w-3.5 text-on-surface-variant hover:text-on-surface" />
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {filteredMatches.length === 0 ? (
                                <TableRow className="border-b border-outline-variant/20">
                                    <TableCell
                                        colSpan={matchColumns.length}
                                        className="h-[252px] text-center align-middle text-on-surface-variant text-sm"
                                    >
                                        {tournamentSelected ? "No results." : "Choose a tournament to display matches."}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {table.getRowModel().rows.map((row) => (
                                        <TableRow
                                            key={row.id}
                                            data-state={row.getIsSelected() && "selected"}
                                            className="border-b border-outline-variant/20 text-on-surface cursor-pointer data-[state=selected]:bg-primary/10 hover:bg-surface-container"
                                            onClick={() => row.toggleSelected()}
                                        >
                                            {row.getVisibleCells().map((cell) => (
                                                <TableCell
                                                    key={cell.id}
                                                    className="h-[42px]"
                                                    style={{ width: `${cell.column.getSize()}px` }}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}

                                    {Array.from({
                                        length: Math.max(0, pageSize - table.getRowModel().rows.length),
                                    }).map((_, i) => (
                                        <TableRow key={`pad-match-${i}`} className="border-b border-outline-variant/20">
                                            <TableCell colSpan={matchColumns.length} className="h-[42px]" />
                                        </TableRow>
                                    ))}
                                </>
                            )}
                            <TableRow className="border-t border-outline-variant/20">
                                <TableCell colSpan={matchColumns.length} className="py-2">
                                    <SelectMatchesDialogPagination
                                        pageIndex={pageIndex}
                                        totalPages={table.getPageCount()}
                                        setPageIndex={setPageIndex}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>
        </div>
    );
}
